import React from 'react';
import { Pressable, Text, View, StyleSheet } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import Feather from '@expo/vector-icons/Feather';
import { useTranslation } from 'react-i18next';
import { dietLabels } from '../../../../utils/dietLabel';
import { Paprika } from '../../../../styles/theme';
import { BottomSheet } from '../primitives/BottomSheet';
import { TimeChip } from '../primitives/TimeChip';
import { SourceBadge } from '../primitives/SourceBadge';
import type { RecipeIndexEntry } from '../../../../services/unifiedRecipeService';
import type { DietaryRole } from '../../../../redux/mealPlanSlice';
import { RemoteImage } from '../../../shared/RemoteImage';

interface Props {
  visible: boolean;
  recipe: RecipeIndexEntry | null;
  dietaryRole?: DietaryRole;
  isCooked?: boolean;
  onClose: () => void;
  onRemove?: () => void;
  onShuffle?: () => void;
  /** Opens the move sheet (#563) — the reporter looked for it here first. */
  onMove?: () => void;
  onExpand?: () => void;
  onCookedToggle?: () => void;
  /** Current rating 0-5 (read-only display when cooked). */
  rating?: number;
  /** Open the full "Comment c'était ?" capture (stars + date + note). */
  onRateRecipe?: () => void;
  /**
   * A recipe offered but not planned yet (the hero's side, #854): the sheet
   * shows it with one action, putting it on the meal. Pass none of the
   * planned-meal actions with it.
   */
  onAdd?: () => void;
}

export function RecipeDetailSheet({ visible, recipe, isCooked, rating, onClose, onRemove, onShuffle, onMove, onExpand, onCookedToggle, onRateRecipe, onAdd }: Props) {
  const { t } = useTranslation();
  if (!recipe) return null;

  return (
    <BottomSheet visible={visible} onClose={onClose} maxHeight="88%">
      {/* Photo — tapping it opens the full recipe screen, like on desktop */}
      <Pressable style={styles.photoWrap} onPress={onExpand} disabled={!onExpand}>
        <RemoteImage
          uri={recipe.image}
          style={styles.photo}
          resizeMode="cover"
          fallback={<View style={[styles.photo, styles.photoPlaceholder]} />}
        />
      </Pressable>

      <View style={styles.content}>
        <Text style={styles.title}>{recipe.title}</Text>

        <View style={styles.metaRow}>
          <SourceBadge source={recipe.sourceUrl} />
          {recipe.readyInMinutes != null && <TimeChip minutes={recipe.readyInMinutes} />}
        </View>

        <View style={styles.tilesRow}>
          <MetaTile label={t('thisWeek.totalLabel')} value={recipe.readyInMinutes ? `${recipe.readyInMinutes} min` : '—'} />
          <MetaTile label={t('thisWeek.portionsLabel')} value={String(recipe.servings ?? 4)} />
        </View>

        {onExpand && (
          <Pressable testID="meal-sheet-view-recipe" style={styles.viewRecipeBtn} onPress={onExpand}>
            <Text style={styles.viewRecipeBtnText}>{t('thisWeek.viewFullRecipe')}</Text>
          </Pressable>
        )}

        {dietLabels(recipe.diets, t).length > 0 && (
          <View style={styles.dietsRow}>
            {dietLabels(recipe.diets, t).slice(0, 4).map((d) => (
              <View key={d} style={styles.dietChip}>
                <Text style={styles.dietChipText}>{d}</Text>
              </View>
            ))}
          </View>
        )}
      </View>

      <View style={styles.footer}>
        {onAdd && (
          <Pressable testID="meal-sheet-add" style={styles.rateRecipeBtn} onPress={onAdd} accessibilityRole="button">
            <Feather name="plus" size={16} color={Paprika.primary} />
            <Text style={styles.rateRecipeBtnText}>{t('thisWeek.addToThisMeal')}</Text>
          </Pressable>
        )}
        {/* Single cook CTA — opens the "Comment c'était ?" capture (marks
            cooked + rates + notes). Replaces the old "J'ai cuisiné ça" toggle. */}
        {onRateRecipe && (
          <Pressable testID="meal-sheet-rate" style={styles.rateRecipeBtn} onPress={onRateRecipe}>
            <Ionicons name="star-outline" size={16} color={Paprika.primary} />
            <Text style={styles.rateRecipeBtnText}>{t('thisWeek.rateThisRecipe')}</Text>
          </Pressable>
        )}
        {isCooked && (
          <View style={styles.cookedMetaRow}>
            {(rating ?? 0) > 0 && (
              <View style={styles.starsRowRO}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <Ionicons
                    key={star}
                    name={star <= (rating ?? 0) ? 'star' : 'star-outline'}
                    size={16}
                    color={star <= (rating ?? 0) ? '#F5C542' : Paprika.textMuted}
                    style={{ marginHorizontal: 1 }}
                  />
                ))}
              </View>
            )}
            {onCookedToggle && (
              <Pressable onPress={onCookedToggle} hitSlop={6}>
                <Text style={styles.unmarkLink}>{t('thisWeek.unmarkCooked')}</Text>
              </Pressable>
            )}
          </View>
        )}
        <View style={styles.footerRow}>
          {onMove && (
            <Pressable style={styles.shuffleBtn} onPress={onMove} accessibilityRole="button">
              <Feather name="calendar" size={14} color={Paprika.textSecondary} />
              <Text style={styles.shuffleBtnText}>{t('thisWeek.move')}</Text>
            </Pressable>
          )}
          {onShuffle && (
            <Pressable style={styles.shuffleBtn} onPress={onShuffle} accessibilityRole="button">
              <Feather name="refresh-cw" size={14} color={Paprika.textSecondary} />
              <Text style={styles.shuffleBtnText}>{t('thisWeek.replace')}</Text>
            </Pressable>
          )}
          {onRemove && (
            <Pressable testID="meal-sheet-remove" style={styles.removeBtn} onPress={onRemove}>
              <Text style={styles.removeBtnText}>{t('thisWeek.remove')}</Text>
            </Pressable>
          )}
        </View>
      </View>
    </BottomSheet>
  );
}

function MetaTile({ label, value }: { label: string; value: string }) {
  return (
    <View style={tileStyles.tile}>
      <Text style={tileStyles.label}>{label}</Text>
      <Text style={tileStyles.value}>{value}</Text>
    </View>
  );
}

const tileStyles = StyleSheet.create({
  tile: {
    flex: 1,
    backgroundColor: Paprika.surfaceLow,
    borderRadius: 8,
    padding: 10,
    alignItems: 'center',
    gap: 2,
  },
  label: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textMuted,
  },
  value: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 16,
    color: Paprika.textPrimary,
  },
});

const styles = StyleSheet.create({
  photoWrap: {
    // Same 12/5 as the meal card, for the same reason (#632): the sheet is now
    // capped at the content column, so the ratio alone bounds the height.
    aspectRatio: 12 / 5,
    position: 'relative',
    backgroundColor: Paprika.surfaceLow,
    borderRadius: 12,
    overflow: 'hidden',
    marginHorizontal: 16,
    marginTop: 8,
  },
  photo: {
    // See MealRow: percentage height against an aspect-derived parent is the
    // one case react-native-web resolves inconsistently.
    ...StyleSheet.absoluteFillObject,
  },
  photoPlaceholder: {
    backgroundColor: Paprika.bgSunk,
  },
  content: {
    padding: 16,
    gap: 12,
  },
  title: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 22,
    lineHeight: 28,
    color: Paprika.textPrimary,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  tilesRow: {
    flexDirection: 'row',
    gap: 8,
  },
  dietsRow: {
    flexDirection: 'row',
    gap: 6,
    flexWrap: 'wrap',
  },
  dietChip: {
    backgroundColor: Paprika.bgSunk,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  dietChipText: {
    fontFamily: 'Inter-Regular',
    fontSize: 11,
    color: Paprika.textSecondary,
  },
  footer: {
    gap: 10,
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 16,
    borderTopWidth: 1,
    borderTopColor: Paprika.border,
  },
  footerRow: {
    flexDirection: 'row',
    gap: 10,
  },
  rateRecipeBtn: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1.5,
    borderColor: Paprika.primary,
    borderRadius: 100,
    paddingVertical: 12,
  },
  rateRecipeBtnText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: Paprika.primary,
  },
  cookedMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
  },
  starsRowRO: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  unmarkLink: {
    fontFamily: 'Inter-Medium',
    fontSize: 12,
    color: Paprika.textMuted,
    textDecorationLine: 'underline',
  },
  shuffleBtn: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
    borderWidth: 1.5,
    borderColor: Paprika.border,
    borderRadius: 100,
    paddingVertical: 13,
    alignItems: 'center',
  },
  shuffleBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: Paprika.textSecondary,
  },
  removeBtn: {
    paddingHorizontal: 16,
    borderWidth: 1.5,
    borderColor: Paprika.border,
    borderRadius: 100,
    paddingVertical: 13,
    alignItems: 'center',
  },
  removeBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 14,
    color: Paprika.textMuted,
  },
  viewRecipeBtn: {
    backgroundColor: Paprika.primary,
    borderRadius: 100,
    paddingVertical: 12,
    alignItems: 'center',
  },
  viewRecipeBtnText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 14,
    color: '#fff',
  },
});
